import { ChevronLeft } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaCheck,
  FaExclamation,
  FaTimes,
  FaInfoCircle,
  FaCalendar,
  FaBook,
} from "react-icons/fa";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import { motion } from "framer-motion";

import axios from "../../../config/axios";

dayjs.locale("fr");

export default function EmargementAppr() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const endpoint = process.env.REACT_APP_URL_IMAGE;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/employes/emargements/projets/${id}`);
        setData(res.data);
        setError(null);
      } catch (error) {
        console.log(error);
        setError("Erreur lors du chargement des données");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Transformer les données pour faciliter l'affichage
  const getFormattedData = () => {
    if (!data || !data.projects) return null;

    const projectItems = data.projects.project_items || [];
    const pourcentage = data.projects.pourcentage || {
      present: "0",
      partiel: "0",
      absent: "0",
    };

    // Extraire les séances uniques
    const seancesMap = new Map();
    projectItems.forEach((item) => {
      if (!seancesMap.has(item.idSeance)) {
        seancesMap.set(item.idSeance, {
          idSeance: item.idSeance,
          dateSeance: item.session_date,
          heureDebut: item.start_hour,
          heureFin: item.end_hour,
        });
      }
    });
    const seances = Array.from(seancesMap.values());

    // Extraire les apprenants uniques et créer une map de présence
    const apprenantsMap = new Map();
    const presenceMap = {};

    projectItems.forEach((item) => {
      // Ajouter l'apprenant s'il n'existe pas déjà
      if (!apprenantsMap.has(item.idEmploye)) {
        apprenantsMap.set(item.idEmploye, {
          idEmploye: item.idEmploye,
          emp_name: `Employé ${item.idEmploye}`, // À remplacer par les vraies données si disponibles
          emp_firstname: "",
          emp_photo: null,
        });
      }

      // Créer la clé de présence
      const key = `${item.idSeance}_${item.idEmploye}`;
      presenceMap[key] = item.isPresent;
    });

    return {
      seances,
      apprenants: Array.from(apprenantsMap.values()),
      presenceMap,
      pourcentage,
      projectInfo: data.projects,
    };
  };

  const formattedData = getFormattedData();

  return (
    <div className="p-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 px-4 py-2"
      >
        <ChevronLeft className="w-5 h-5" />
        Retour
      </button>

      <h1 className="text-3xl text-center font-semibold text-gray-800">
        Fiche de présence
      </h1>

      <div className="flex items-center justify-between mb-6 mt-10">
        <div className="flex items-center gap-4">
          <div>
            {/* Informations du projet */}
            {data?.projects && (
              <div className="flex flex-wrap items-center gap-4 mt-3">
                {/* Dates - à adapter selon vos données réelles */}
                {formattedData?.seances?.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-purple-50 border border-purple-200 rounded-lg">
                    <FaCalendar className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-700">
                      {dayjs(formattedData.seances[0].dateSeance).format(
                        "DD MMM YYYY",
                      )}{" "}
                      -{" "}
                      {dayjs(
                        formattedData.seances[formattedData.seances.length - 1]
                          .dateSeance,
                      ).format("DD MMM YYYY")}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Contenu de la fiche de présence */}
      <div className="space-y-6">
        {/* Instructions d'utilisation simplifiées */}
        {showInstructions && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-blue-50 border border-blue-200 rounded-lg"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <FaInfoCircle className="w-5 h-5 mt-0.5 text-blue-600 flex-shrink-0" />
                <div className="space-y-2">
                  <h3 className="font-semibold text-blue-800">
                    Fiche de présence - Consultation uniquement
                  </h3>
                  <p className="text-sm text-blue-700">
                    Cette fiche affiche les présences des apprenants. Les
                    couleurs indiquent le statut de présence pour chaque séance.
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowInstructions(false)}
                className="p-1 text-blue-400 rounded hover:text-blue-600 hover:bg-blue-100"
                aria-label="Masquer les instructions"
              >
                <FaTimes className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        )}

        {/* Messages de chargement / erreur */}
        {loading ? (
          <div className="py-10 italic text-center text-gray-500">
            Chargement des données de présence...
          </div>
        ) : error ? (
          <div className="py-4 text-center text-red-500 border border-red-200 rounded bg-red-50">
            {error}
          </div>
        ) : !formattedData || formattedData.seances.length === 0 ? (
          <div className="py-10 italic text-center text-gray-400">
            Pas de session pour ce projet
          </div>
        ) : (
          <>
            {/* Légendes */}
            <div className="flex flex-wrap justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 font-medium text-green-600">
                  <FaCheck /> Présent {formattedData.pourcentage.present}%
                </div>
                <div className="flex items-center gap-2 font-medium text-yellow-500">
                  <FaExclamation /> Partiellement{" "}
                  {formattedData.pourcentage.partiel}%
                </div>
                <div className="flex items-center gap-2 font-medium text-red-400">
                  <FaTimes /> Absent {formattedData.pourcentage.absent}%
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex items-center gap-2 text-gray-400">
                  <div className="w-4 h-4 bg-gray-400 rounded"></div> Non défini
                </span>
                <span className="flex items-center gap-2 text-green-400">
                  <div className="w-4 h-4 bg-green-400 rounded"></div> Présent
                </span>
                <span className="flex items-center gap-2 text-yellow-400">
                  <div className="w-4 h-4 bg-yellow-400 rounded"></div>{" "}
                  Partiellement
                </span>
                <span className="flex items-center gap-2 text-red-400">
                  <div className="w-4 h-4 bg-red-400 rounded"></div> Absent
                </span>
              </div>
            </div>

            {/* Tableau - Version lecture seule */}
            <div className="overflow-x-auto text-sm border border-gray-200 shadow-sm rounded-xl">
              <table className="min-w-full text-gray-700">
                <thead className="border-b border-gray-200 bg-gray-50">
                  <tr>
                    <th className="p-3 font-semibold text-left">Apprenant</th>
                    {formattedData.seances.map((seance) => (
                      <th
                        key={seance.idSeance}
                        className="p-3 font-semibold text-center"
                      >
                        <div>
                          {dayjs(seance.dateSeance).format("DD MMM YYYY")}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr className="text-gray-500 bg-white">
                    <td className="p-3 font-medium">Heure début</td>
                    {formattedData.seances.map((seance) => (
                      <td
                        key={`start-${seance.idSeance}`}
                        className="p-3 text-center"
                      >
                        {seance.heureDebut}
                      </td>
                    ))}
                  </tr>
                  <tr className="text-gray-500 bg-white border-b border-gray-200">
                    <td className="p-3 font-medium">Heure fin</td>
                    {formattedData.seances.map((seance) => (
                      <td
                        key={`end-${seance.idSeance}`}
                        className="p-3 text-center"
                      >
                        {seance.heureFin}
                      </td>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {formattedData.apprenants.length === 0 ? (
                    <tr>
                      <td
                        colSpan={formattedData.seances.length + 1}
                        className="py-6 italic text-center text-gray-400"
                      >
                        Aucun apprenant trouvé
                      </td>
                    </tr>
                  ) : (
                    formattedData.apprenants.map((apprentice) => (
                      <tr
                        key={apprentice.idEmploye}
                        className="border-b hover:bg-gray-50"
                      >
                        <td className="flex items-center gap-3 p-3 font-medium text-gray-800">
                          <div className="flex items-center justify-center w-10 h-10 overflow-hidden text-sm font-semibold text-gray-600 uppercase bg-gray-100 rounded-full">
                            {apprentice.emp_photo ? (
                              <img
                                src={`${endpoint}/img/employes/${apprentice.emp_photo}`}
                                alt=""
                                className="object-cover w-full h-full"
                              />
                            ) : (
                              `E${apprentice.idEmploye}`
                            )}
                          </div>
                          {apprentice.emp_name ||
                            `Employé ${apprentice.idEmploye}`}{" "}
                          {apprentice.emp_firstname}
                        </td>
                        {formattedData.seances.map((seance) => {
                          const key = `${seance.idSeance}_${apprentice.idEmploye}`;
                          const rawStatus = formattedData.presenceMap[key];
                          const status =
                            rawStatus === null || rawStatus === undefined
                              ? "undefined"
                              : parseInt(rawStatus);

                          const getStatusColor = (status) => {
                            switch (status) {
                              case 3:
                                return "bg-green-500";
                              case 2:
                                return "bg-yellow-400";
                              case 1:
                                return "bg-red-500";
                              case "undefined":
                                return "bg-gray-300";
                              default:
                                return "bg-gray-300";
                            }
                          };

                          const getStatusIcon = (status) => {
                            switch (status) {
                              case 3:
                                return (
                                  <FaCheck className="text-white text-xs" />
                                );
                              case 2:
                                return (
                                  <FaExclamation className="text-white text-xs" />
                                );
                              case 1:
                                return (
                                  <FaTimes className="text-white text-xs" />
                                );
                              default:
                                return null;
                            }
                          };

                          return (
                            <td key={key} className="p-3 text-center">
                              <div
                                className={`w-6 h-6 mx-auto flex items-center justify-center rounded-md ${getStatusColor(
                                  status,
                                )}`}
                              >
                                {getStatusIcon(status)}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
